import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface TourStep {
  icon: LucideIcon;
  title: string;
  description: string;
}

const defaultGradients = [
  "from-primary to-primary/70",
  "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600",
  "from-blue-500 to-indigo-600",
  "from-violet-500 to-purple-600",
  "from-rose-500 to-pink-600",
  "from-cyan-500 to-sky-600",
  "from-green-500 to-emerald-600",
];

interface FeatureTourModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: TourStep[];
  iconGradients?: string[];
}

export default function FeatureTourModal({ open, onOpenChange, steps, iconGradients }: FeatureTourModalProps) {
  const [step, setStep] = useState(0);
  const gradients = iconGradients || defaultGradients;

  const goNext = () => {
    if (step < steps.length - 1) {
      setStep(s => s + 1);
    } else {
      onOpenChange(false);
      setStep(0);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setStep(s => s - 1);
    }
  };

  const current = steps[step];
  if (!current) return null;
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setStep(0); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl">
        <DialogTitle className="sr-only">How It Works</DialogTitle>

        <div className="px-6 pt-5 pb-0 flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
            {step + 1} of {steps.length}
          </span>
          <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="px-6 py-6 min-h-[260px] flex flex-col items-center text-center">
          <div className="flex flex-col items-center" key={step}>
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradients[step % gradients.length]} flex items-center justify-center mb-5 shadow-lg`}>
              <Icon className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3">{current.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              {current.description}
            </p>
          </div>
        </div>

        <div className="px-6 pb-5 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={goBack} disabled={step === 0} className="gap-1">
            <ChevronLeft className="w-4 h-4" /> Back
          </Button>
          <Button size="sm" onClick={goNext} className="gap-1">
            {step === steps.length - 1 ? "Get Started" : "Next"}
            {step < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
